import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';
import { toast } from '../utils/toast';
import { extractApiError } from '../utils/apiError';
import type { Project } from '../store/useProjectsStore';

// ─── Project CRUD ───────────────────────────────────────────────────────────

export type ApiProjectType =
  | 'event'
  | 'tour'
  | 'music_video'
  | 'album_rollout'
  | 'festival'
  | 'custom';

export type ApiProjectStatus = 'active' | 'planning' | 'completed' | 'archived';

/** Per-project capability flags returned by the Get Project Details endpoint — the
 * single source of truth for what the current user may do with this project. */
export interface ProjectPermissions {
  can_view: boolean;
  can_edit: boolean;
  can_archive: boolean;
  can_delete: boolean;
  can_edit_linked_event: boolean;
  can_invite_manager: boolean;
  can_invite_editor: boolean;
  can_invite_guest: boolean;
  can_remove_manager: boolean;
  can_remove_editor: boolean;
  can_remove_guest: boolean;
  can_change_member_role: boolean;
}

export interface ApiProject {
  uuid: string;
  workspace_uuid: string;
  created_by_id: number;
  name: string;
  // Some responses may return a single type; others still return an array.
  project_type: ApiProjectType | ApiProjectType[];
  custom_project_type?: string | null;
  description: string;
  image: string | null;
  start_date: string | null;
  end_date: string | null;
  status: ApiProjectStatus;
  show_budget_to_collaborators: boolean;
  linked_event: Record<string, unknown> | null;
  collaborators: Record<string, unknown>[] | null | undefined;
  members: unknown[] | null | undefined;
  workspace_role?: string | null;
  project_role?: string | null;
  permissions?: ProjectPermissions;
  created_at: string;
}

export interface TabCounts {
  all?: number;
  mine?: number;
  shared?: number;
  archived?: number;
}

export interface PaginatedProjectsResponse {
  success?: boolean;
  tab_counts?: TabCounts;
  count: number;
  next: string | null;
  previous: string | null;
  results: ApiProject[];
}

export interface ProjectListParams {
  page?: number;
  page_size?: number;
  tab?: 'all' | 'archived' | 'mine' | 'shared';
}

export const useGetProjects = (
  params: ProjectListParams = {},
  options: { enabled?: boolean } = {},
) => {
  return useQuery<PaginatedProjectsResponse>({
    queryKey: ['projects', 'list', params],
    enabled: options.enabled,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      if (params.page_size) searchParams.set('page_size', String(params.page_size));
      if (params.tab) searchParams.set('tab', params.tab);
      const qs = searchParams.toString();
      const { data } = await apiClient.get<
        PaginatedProjectsResponse | { data: PaginatedProjectsResponse }
      >(`/projects/${qs ? `?${qs}` : ''}`);
      // Some responses on this API wrap the payload in { data: ... } — unwrap defensively.
      return (
        (data as { data?: PaginatedProjectsResponse }).data ?? (data as PaginatedProjectsResponse)
      );
    },
  });
};

export const useGetProject = (projectUuid: string | undefined) => {
  return useQuery<ApiProject>({
    queryKey: ['projects', projectUuid],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiProject | { data: ApiProject }>(
        `/projects/${projectUuid}/`,
      );
      // Some responses on this API wrap the payload in { data: ... } — unwrap defensively.
      return (data as { data?: ApiProject }).data ?? (data as ApiProject);
    },
    enabled: !!projectUuid,
  });
};

export interface ProjectMutationPayload {
  workspace_uuid?: string | null;
  name?: string;
  project_type?: ApiProjectType | ApiProjectType[];
  custom_project_type?: string;
  description?: string;
  image?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  status?: ApiProjectStatus;
  show_budget_to_collaborators?: boolean;
  linked_event_uuid?: string | null;
  collaborator_ids?: number[];
}

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ProjectMutationPayload) => {
      const { data } = await apiClient.post<{ data: ApiProject }>('/projects/', payload);
      return data.data;
    },
    onSuccess: () => {
      toast.success('Project created successfully');
      queryClient.invalidateQueries({ queryKey: ['projects', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to create project'));
    },
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectUuid,
      payload,
    }: {
      projectUuid: string;
      payload: ProjectMutationPayload;
    }) => {
      const { data } = await apiClient.patch<{ data: ApiProject }>(
        `/projects/${projectUuid}/`,
        payload,
      );
      return data.data;
    },
    onSuccess: (_data, { projectUuid }) => {
      toast.success('Project updated successfully');
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to update project'));
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (projectUuid: string) => {
      await apiClient.delete(`/projects/${projectUuid}/`);
      return projectUuid;
    },
    onSuccess: () => {
      toast.success('Project deleted');
      queryClient.invalidateQueries({ queryKey: ['projects', 'list'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to delete project'));
    },
  });
};

// ─── Mapping: API project → local UI Project shape ─────────────────────────

const PROJECT_TYPE_LABELS: Record<ApiProjectType, Project['subtitle']> = {
  event: 'Event',
  tour: 'Tour',
  music_video: 'Music Video',
  album_rollout: 'Album Rollout',
  festival: 'Festival',
  custom: 'Custom',
};

export const PROJECT_TYPE_IMAGES: Record<ApiProjectType, string> = {
  event:
    'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&w=800&q=80',
  tour: 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?auto=format&fit=crop&w=800&q=80',
  music_video:
    'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=800&q=80',
  album_rollout:
    'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&w=800&q=80',
  festival:
    'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?auto=format&fit=crop&w=800&q=80',
  custom:
    'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&w=800&q=80',
};

export const PROJECT_TYPE_LABEL_TO_API: Record<Project['subtitle'], ApiProjectType> = {
  Event: 'event',
  Tour: 'tour',
  'Music Video': 'music_video',
  'Album Rollout': 'album_rollout',
  Festival: 'festival',
  Custom: 'custom',
};

const STATUS_LABELS: Record<ApiProjectStatus, Project['status']> = {
  active: 'Active',
  planning: 'Planning',
  completed: 'Completed',
  // 'archived' has no local equivalent — isArchived is tracked separately, so
  // fall back to a sensible display status for the badge.
  archived: 'Active',
};

export const PROJECT_STATUS_LABEL_TO_API: Record<
  Project['status'],
  'active' | 'planning' | 'completed'
> = {
  Active: 'active',
  Planning: 'planning',
  Completed: 'completed',
};

/**
 * Numeric stats (progress, task counts, budget) have no corresponding field
 * in the Projects API — it only tracks project identity. These are kept as a
 * small demo overlay keyed by uuid so the one seeded staging project (backed
 * by the mocked tasks/budget in useProjectsStore) renders identically to
 * before; every other real project falls back to zero. Crew count is real —
 * it comes from the `members` array the API returns per project.
 */
const PROJECT_DEMO_STATS: Record<
  string,
  {
    progress: number;
    taskCount: number;
    completedTaskCount: number;
    allocatedBudget: number;
    spentBudget: number;
  }
> = {
  '7987246c-9af5-489b-be85-d40a54e1681e': {
    progress: 68,
    taskCount: 24,
    completedTaskCount: 16,
    allocatedBudget: 50000,
    spentBudget: 31450,
  },
};

const extractLinkedEventTitle = (
  linkedEvent: Record<string, unknown> | null | undefined,
): string | undefined => {
  if (!linkedEvent) return undefined;
  const title = linkedEvent.title ?? linkedEvent.event_title ?? linkedEvent.name;
  return typeof title === 'string' && title ? title : undefined;
};

const extractLinkedEventUuid = (
  linkedEvent: Record<string, unknown> | null | undefined,
): string | null => {
  if (!linkedEvent) return null;
  const uuid = linkedEvent.event_uuid ?? linkedEvent.uuid ?? linkedEvent.id;
  if (typeof uuid === 'string') return uuid;
  if (typeof uuid === 'number') return String(uuid);
  return null;
};

const extractCollaboratorIds = (
  collaborators: Record<string, unknown>[] | null | undefined,
): number[] => {
  if (!Array.isArray(collaborators)) return [];
  return collaborators
    .map((c) => c.id ?? c.user_id)
    .filter((id): id is number => typeof id === 'number');
};

const projectTypesToLabels = (
  projectType: ApiProjectType | ApiProjectType[],
): Project['subtitle'][] => {
  const types = Array.isArray(projectType) ? projectType : [projectType];
  const labels = types.map((t) => PROJECT_TYPE_LABELS[t]).filter(Boolean);
  return labels.length > 0 ? labels : ['Custom'];
};

export const canUserArchiveProject = (
  project?: Project | null,
  workspaceRole?: string | null,
): boolean => {
  if (!project) return false;
  if (project.permissions && project.permissions.can_archive === false) {
    return false;
  }
  const effectiveWorkspaceRole = workspaceRole || project.workspaceRole;
  const isEditor = effectiveWorkspaceRole === 'editor' || project.projectRole === 'editor';
  if (isEditor && !project.isMyProject) {
    return false;
  }
  return true;
};

export const mapApiProjectToProject = (
  apiProject: ApiProject,
  currentUserId?: string | number,
): Project => {
  const demoStats = PROJECT_DEMO_STATS[apiProject.uuid];
  const isMyProject =
    currentUserId != null && String(apiProject.created_by_id) === String(currentUserId);
  const isArchived = apiProject.status === 'archived';
  const projectTypes = projectTypesToLabels(apiProject.project_type);
  const primaryApiType = Array.isArray(apiProject.project_type)
    ? (apiProject.project_type[0] ?? 'custom')
    : apiProject.project_type;

  const isEditor = apiProject.workspace_role === 'editor' || apiProject.project_role === 'editor';
  const rawCanArchive = apiProject.permissions?.can_archive ?? true;
  const canArchive = rawCanArchive && (!isEditor || isMyProject);

  const permissions = apiProject.permissions
    ? {
        ...apiProject.permissions,
        can_archive: canArchive,
      }
    : undefined;

  return {
    id: apiProject.uuid,
    uuid: apiProject.uuid,
    workspaceUuid: apiProject.workspace_uuid,
    title: apiProject.name,
    subtitle: projectTypes[0],
    projectTypes,
    status: STATUS_LABELS[apiProject.status],
    customProjectType: apiProject.custom_project_type || '',
    workspaceRole: apiProject.workspace_role,
    projectRole: apiProject.project_role,
    permissions,
    startDate: formatIsoDateToHuman(apiProject.start_date),
    endDate: formatIsoDateToHuman(apiProject.end_date),
    progress: demoStats?.progress ?? 0,
    crewCount: Array.isArray(apiProject.members) ? apiProject.members.length : 0,
    taskCount: demoStats?.taskCount ?? 0,
    completedTaskCount: demoStats?.completedTaskCount ?? 0,
    allocatedBudget: demoStats?.allocatedBudget ?? 0,
    spentBudget: demoStats?.spentBudget ?? 0,
    bgImage: apiProject.image || PROJECT_TYPE_IMAGES[primaryApiType],
    description: apiProject.description || '',
    linkedEvent: extractLinkedEventTitle(apiProject.linked_event),
    linkedEventUuid: extractLinkedEventUuid(apiProject.linked_event),
    isMyProject,
    isShared: !isMyProject,
    isArchived,
    showBudgetToCollaborators: apiProject.show_budget_to_collaborators,
    collaboratorIds: extractCollaboratorIds(apiProject.collaborators),
    rawStartDate: apiProject.start_date,
    rawEndDate: apiProject.end_date,
  };
};

const formatIsoDateToHuman = (iso: string | null): string => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export interface TicketSaleTypeSummary {
  ticket_type: string;
  quantity: number;
  revenue: number | string;
}

export interface TicketSaleSummary {
  total_revenue: number | string;
  total_tickets_sold: number;
  ticket_types: TicketSaleTypeSummary[];
  net_profit_loss: string | number | null;
  revenue_vs_expense: string | null;
}

export interface TicketSaleItem {
  customer_name: string;
  customer_email: string;
  ticket_type: string;
  quantity: number;
  revenue: string | number;
  payment_method: string;
  purchased_at: string;
}

export interface ProjectTicketSalesResponse {
  success: boolean;
  summary: TicketSaleSummary;
  count: number;
  next: string | null;
  previous: string | null;
  results: TicketSaleItem[];
}

export interface ProjectTicketSalesParams {
  projectUuid: string;
  amount_max?: number;
  amount_min?: number;
  date_from?: string;
  date_to?: string;
  include_refunded?: boolean;
  page?: number;
  page_size?: number;
  search?: string;
  ticket_type?: string;
}

export const useGetProjectTicketSales = (
  params: ProjectTicketSalesParams,
  options?: { enabled?: boolean },
) => {
  const isEnabled = options?.enabled !== undefined ? options.enabled : !!params.projectUuid;
  return useQuery<ProjectTicketSalesResponse>({
    queryKey: ['projects', params.projectUuid, 'ticket-sales', params],
    enabled: isEnabled,
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      if (
        params.amount_max !== undefined &&
        params.amount_max !== null &&
        !isNaN(params.amount_max)
      ) {
        searchParams.set('amount_max', String(params.amount_max));
      }
      if (
        params.amount_min !== undefined &&
        params.amount_min !== null &&
        !isNaN(params.amount_min)
      ) {
        searchParams.set('amount_min', String(params.amount_min));
      }
      if (params.date_from) {
        searchParams.set('date_from', params.date_from);
      }
      if (params.date_to) {
        searchParams.set('date_to', params.date_to);
      }
      if (params.include_refunded !== undefined) {
        searchParams.set('include_refunded', String(params.include_refunded));
      }
      if (params.page) {
        searchParams.set('page', String(params.page));
      }
      if (params.page_size) {
        searchParams.set('page_size', String(params.page_size));
      }
      if (params.search) {
        searchParams.set('search', params.search);
      }
      if (params.ticket_type && params.ticket_type !== 'All') {
        searchParams.set('ticket_type', params.ticket_type);
      }

      const queryString = searchParams.toString();
      const url = `/projects/${params.projectUuid}/ticket-sales/${queryString ? `?${queryString}` : ''}`;
      const { data } = await apiClient.get<ProjectTicketSalesResponse>(url);
      return data;
    },
  });
};

// ─── Project Members & Invitations ─────────────────────────────────────────

/** Assignable project roles. The creator/owner is implicit and not part of this enum. */
export type ProjectRole = 'manager' | 'editor' | 'guest';

export interface ProjectMemberUser {
  id: number;
  username: string | null;
  full_name: string | null;
  email: string;
}

export interface ApiProjectMember {
  uuid: string;
  role: ProjectRole;
  user: ProjectMemberUser;
  created_at: string;
  updated_at: string;
}

export interface PaginatedProjectMembersResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ApiProjectMember[];
}

export type ProjectInvitationStatus = 'pending' | 'accepted' | 'cancelled' | 'expired';

export interface ApiProjectInvitation {
  uuid: string;
  email: string;
  role: ProjectRole;
  status: ProjectInvitationStatus;
  workspace_uuid: string;
  project_uuid: string | null;
  project_name: string | null;
  invitee: ProjectMemberUser;
  invited_by: ProjectMemberUser;
  is_automatic?: boolean;
  automatic?: boolean;
  first_seen_at: string | null;
  expires_at: string;
  created_at: string;
  updated_at: string;
}

export interface PaginatedProjectInvitationsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ApiProjectInvitation[];
}

export interface ProjectMembersParams {
  projectUuid: string;
  page?: number;
  page_size?: number;
}

export const useGetProjectMembers = (
  params: ProjectMembersParams,
  options?: { enabled?: boolean },
) => {
  const isEnabled = options?.enabled !== undefined ? options.enabled : !!params.projectUuid;
  return useQuery<PaginatedProjectMembersResponse>({
    queryKey: ['projects', params.projectUuid, 'members', params],
    enabled: isEnabled,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      if (params.page_size) searchParams.set('page_size', String(params.page_size));
      const qs = searchParams.toString();
      const { data } = await apiClient.get<
        PaginatedProjectMembersResponse | { data: PaginatedProjectMembersResponse }
      >(`/projects/${params.projectUuid}/members/${qs ? `?${qs}` : ''}`);
      return (
        (data as { data?: PaginatedProjectMembersResponse }).data ??
        (data as PaginatedProjectMembersResponse)
      );
    },
  });
};

export const useUpdateProjectMemberRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectUuid,
      memberUuid,
      role,
    }: {
      projectUuid: string;
      memberUuid: string;
      role: ProjectRole;
    }) => {
      const { data } = await apiClient.patch<{ data: ApiProjectMember } | ApiProjectMember>(
        `/projects/${projectUuid}/members/${memberUuid}/`,
        { role },
      );
      return (data as { data?: ApiProjectMember }).data ?? (data as ApiProjectMember);
    },
    onSuccess: (_data, { projectUuid }) => {
      toast.success('Member role updated');
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid, 'members'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to update member role'));
    },
  });
};

export const useRemoveProjectMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectUuid,
      memberUuid,
    }: {
      projectUuid: string;
      memberUuid: string;
    }) => {
      await apiClient.delete(`/projects/${projectUuid}/members/${memberUuid}/`);
      return memberUuid;
    },
    onSuccess: (_data, { projectUuid }) => {
      toast.success('Member removed');
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid, 'members'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to remove member'));
    },
  });
};

export interface ProjectInvitationsParams {
  projectUuid: string;
  page?: number;
  page_size?: number;
}

export const useGetProjectInvitations = (
  params: ProjectInvitationsParams,
  options?: { enabled?: boolean },
) => {
  const isEnabled = options?.enabled !== undefined ? options.enabled : !!params.projectUuid;
  return useQuery<PaginatedProjectInvitationsResponse>({
    queryKey: ['projects', params.projectUuid, 'invitations', params],
    enabled: isEnabled,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      if (params.page_size) searchParams.set('page_size', String(params.page_size));
      const qs = searchParams.toString();
      const { data } = await apiClient.get<
        PaginatedProjectInvitationsResponse | { data: PaginatedProjectInvitationsResponse }
      >(`/projects/${params.projectUuid}/invitations/${qs ? `?${qs}` : ''}`);
      return (
        (data as { data?: PaginatedProjectInvitationsResponse }).data ??
        (data as PaginatedProjectInvitationsResponse)
      );
    },
  });
};

export const useCreateProjectInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectUuid,
      email,
      role,
    }: {
      projectUuid: string;
      email: string;
      role: ProjectRole;
    }) => {
      const { data } = await apiClient.post<{ data: ApiProjectInvitation } | ApiProjectInvitation>(
        `/projects/${projectUuid}/invitations/`,
        { email, role },
      );
      return (data as { data?: ApiProjectInvitation }).data ?? (data as ApiProjectInvitation);
    },
    onSuccess: (_data, { projectUuid }) => {
      toast.success('Invitation sent');
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid, 'invitations'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to send invitation'));
    },
  });
};

export const useCancelProjectInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectUuid,
      invitationUuid,
    }: {
      projectUuid: string;
      invitationUuid: string;
    }) => {
      await apiClient.delete(`/projects/${projectUuid}/invitations/${invitationUuid}/`);
      return invitationUuid;
    },
    onSuccess: (_data, { projectUuid }) => {
      toast.success('Invitation cancelled');
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid, 'invitations'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to cancel invitation'));
    },
  });
};

export const useResendProjectInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectUuid,
      invitationUuid,
    }: {
      projectUuid: string;
      invitationUuid: string;
    }) => {
      const { data } = await apiClient.post<{ data: ApiProjectInvitation } | ApiProjectInvitation>(
        `/projects/${projectUuid}/invitations/${invitationUuid}/resend/`,
      );
      return (data as { data?: ApiProjectInvitation }).data ?? (data as ApiProjectInvitation);
    },
    onSuccess: (_data, { projectUuid }) => {
      toast.success('Invitation resent');
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid, 'invitations'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to resend invitation'));
    },
  });
};
